import { VALID_GUESSES } from '../constants/validGuesses'
import {
  HIDDEN_WORDS,
  DAILY_ORDER,
  HiddenWord,
} from '../constants/hiddenWords'
import { WRONG_SPOT_MESSAGE, NOT_CONTAINED_MESSAGE } from '../constants/strings'
import { getGuessStatuses } from './statuses'
import { default as GraphemeSplitter } from 'grapheme-splitter'

// Every curated Hidden Word (already lowercase) is itself a valid guess.
const HIDDEN_WORD_SET = new Set(HIDDEN_WORDS.map((w) => w.word))
const HIDDEN_WORD_BY_ID: { [id: string]: HiddenWord } = Object.fromEntries(
  HIDDEN_WORDS.map((w) => [w.id, w])
)

export const isWordInWordList = (word: string) => {
  const lower = localeAwareLowerCase(word)
  return HIDDEN_WORD_SET.has(lower) || VALID_GUESSES.includes(lower)
}

export const isWinningWord = (word: string) => {
  return solution === word
}

// build a set of previously revealed letters - present and correct
// guess must use correct letters in that space and any other revealed letters
// also check if all revealed instances of a letter are used (i.e. two C's)
export const findFirstUnusedReveal = (word: string, guesses: string[]) => {
  if (guesses.length === 0) {
    return false
  }

  const lettersLeftArray = new Array<string>()
  const guess = guesses[guesses.length - 1]
  const statuses = getGuessStatuses(guess)
  const splitWord = unicodeSplit(word)
  const splitGuess = unicodeSplit(guess)

  for (let i = 0; i < splitGuess.length; i++) {
    if (statuses[i] === 'correct' || statuses[i] === 'present') {
      lettersLeftArray.push(splitGuess[i])
    }
    if (statuses[i] === 'correct' && splitWord[i] !== splitGuess[i]) {
      return WRONG_SPOT_MESSAGE(splitGuess[i], i + 1)
    }
  }

  // check for the first unused letter, taking duplicate letters
  // into account - see issue #198
  let n
  for (const letter of splitWord) {
    n = lettersLeftArray.indexOf(letter)
    if (n !== -1) {
      lettersLeftArray.splice(n, 1)
    }
  }

  if (lettersLeftArray.length > 0) {
    return NOT_CONTAINED_MESSAGE(lettersLeftArray[0])
  }
  return false
}

export const unicodeSplit = (word: string) => {
  return new GraphemeSplitter().splitGraphemes(word)
}

export const unicodeLength = (word: string) => {
  return unicodeSplit(word).length
}

export const localeAwareLowerCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleLowerCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toLowerCase()
}

export const localeAwareUpperCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleUpperCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toUpperCase()
}

export const getWordOfDay = () => {
  // January 1, 2022 Game Epoch (local midnight)
  const epochMs = new Date(2022, 0).valueOf()
  const now = Date.now()
  const msInDay = 86400000
  const index = Math.floor((now - epochMs) / msInDay)
  const nextday = (index + 1) * msInDay + epochMs

  // The puzzle number IS the daily index — dated, stable, and no spoiler.
  const id = DAILY_ORDER[index % DAILY_ORDER.length]
  const entry = HIDDEN_WORD_BY_ID[id]

  return {
    solution: localeAwareUpperCase(entry.word),
    puzzleNumber: index,
    passage: entry,
    tomorrow: nextday,
  }
}

export const { solution, puzzleNumber, passage, tomorrow } = getWordOfDay()
